import React from 'react';
import isNil from 'lodash.isnil';
import isEqual from 'lodash.isequal';
import { differenceInCalendarDays } from 'date-fns';
import get from 'lodash.get';
import isEmpty from 'lodash.isempty';
import { MARK_TYPE, EVENT_TYPES } from '../../constants/shared/ProbeDetailsConstants';
import { deletePunctuationCharacters } from '../../features/probeDetails/reading/tro/troProbeDetailsHelper';
import { convertKeysFromCamelToUndescore, getWordEnding } from '../shared/commonUtils';
import {
    ADDITIONAL_BOOK_MODAL,
    CANNOT_BE_EDIT_MESSAGE,
    CHANGED_PERFOMANCE_MODAL,
    ON_GRADE_TEXT,
    TRO_BANNER_CONTENT,
    TRO_EOY_CAN_EDIT_TEXT,
} from '../../constants/reading/TroConstants';
import { getNextPeriod, PERIOD } from '../../constants/shared/CommonConstants';

const { UNTRANSCRIBABLE_BANNER, ON_GRADE_TEXT_BANNER, HIGH_OR_LOW_TEXT_BANNER } =
    TRO_BANNER_CONTENT;
const { INSERT, SUBSTITUTE, OMIT, CORRECT } = MARK_TYPE;
const { USER_EVENT, VR_EVENT } = EVENT_TYPES;

export const getEventPosition = eventDetails =>
    get(eventDetails, 'startPosition', eventDetails.position);

const getModifiedEvent = (event, position, startIsInsert, endIsInsert) => ({
    ...event,
    eventDetails: {
        ...event.eventDetails,
        ...(!isNil(startIsInsert) &&
            !isNil(endIsInsert) && {
                startIsInsert,
                endIsInsert,
            }),
        startPosition: position,
        endPosition: position,
    },
});

/**
 * Split events so that each event(omit, correct) doesn't have a position as a range
 * @param {Array} itemLevelDetails list of events
 * @returns list of splited events
 */
export const getSplitedEvents = itemLevelDetails =>
    itemLevelDetails.reduce((accumulator, currentValue) => {
        let newEvents = [];
        const { eventType, eventDetails } = currentValue;
        const { startPosition, endPosition, startIsInsert, endIsInsert } = eventDetails;

        if (isNil(startPosition) || endPosition === startPosition) {
            newEvents = [currentValue];
        } else {
            for (let position = startPosition; position <= endPosition; position += 1) {
                const eventsByPosition =
                    eventType === CORRECT &&
                    itemLevelDetails.filter(
                        event =>
                            getEventPosition(event.eventDetails) === position &&
                            event.eventType !== CORRECT,
                    );
                switch (!isEmpty(eventsByPosition) && eventsByPosition.length) {
                    case 1:
                        newEvents.push(
                            getModifiedEvent(
                                currentValue,
                                position,
                                eventsByPosition[0].eventType === INSERT,
                                eventsByPosition[0].eventType === INSERT,
                            ),
                        );
                        break;
                    case 2:
                        newEvents.push(getModifiedEvent(currentValue, position, true, true));
                        newEvents.push(getModifiedEvent(currentValue, position, false, false));
                        break;

                    default:
                        newEvents.push(
                            getModifiedEvent(currentValue, position, startIsInsert, endIsInsert),
                        );
                        break;
                }
            }
        }
        return [...accumulator, ...newEvents];
    }, []);

const getSingleMarkPerWord = itemLevelDetails =>
    itemLevelDetails.filter(markObj => {
        const { eventDetails, eventType } = markObj;

        if (eventDetails.source === USER_EVENT) return true; // User marks have higher priority than VR marks
        if (eventType !== OMIT && eventType !== SUBSTITUTE) return true; // only OMIT or SUBSTITUTE should be single per word

        // check if for current word exist OMIT or SUBSTITUTE user mark
        const userMarkForCurrentPosition = itemLevelDetails.find(mark => {
            const isOmitOrSubstitute = eventType === OMIT || eventType === SUBSTITUTE;
            return (
                mark.eventDetails.source === USER_EVENT &&
                isOmitOrSubstitute &&
                getEventPosition(mark.eventDetails) === getEventPosition(eventDetails)
            );
        });
        // if no then we can return VR mark otherwise skip this VR mark
        return isNil(userMarkForCurrentPosition);
    });

export const generateReadingStats = (itemLevelDetails, listOfWords, startPosition, endPosition) => {
    const omissions = [];
    const insertions = [];
    const subtitutions = [];
    const numberOfWords =
        isNil(endPosition) || isNil(startPosition) ? 0 : endPosition - startPosition + 1;

    if (!isEmpty(listOfWords) && numberOfWords !== 0) {
        const correctEventsPositions = itemLevelDetails
            .filter(({ eventType }) => eventType === CORRECT)
            .map(({ eventDetails }) => getEventPosition(eventDetails));
        getSingleMarkPerWord(itemLevelDetails)
            .sort(
                (currentEvent, nextEvent) =>
                    getEventPosition(currentEvent.eventDetails) -
                    getEventPosition(nextEvent.eventDetails),
            )
            .forEach(({ eventDetails, eventType }) => {
                const eventPosition = getEventPosition(eventDetails);
                const isCorrect = eventDetails.source === USER_EVENT;

                switch (!correctEventsPositions.includes(eventPosition) && eventType) {
                    case OMIT:
                        omissions.push({
                            word: deletePunctuationCharacters(listOfWords[eventPosition].word),
                            isCorrect,
                        });
                        break;
                    case INSERT:
                        insertions.push({
                            word: eventDetails.annotationContent,
                            isCorrect,
                        });
                        break;
                    case SUBSTITUTE:
                        subtitutions.push({
                            word: deletePunctuationCharacters(listOfWords[eventPosition].word),
                            isCorrect,
                        });
                        break;
                    default:
                        break;
                }
            });
    }

    return {
        insertions,
        subtitutions,
        omissions,
        correctWords: numberOfWords - subtitutions.length - omissions.length,
    };
};

export const splitBooksInformationByValid = booksInformation => {
    const validBooksInformation = [];
    const invalidBooksInformation = [];

    booksInformation.forEach(book => {
        if (book.isValid) {
            validBooksInformation.push(book);
        } else {
            invalidBooksInformation.push(book);
        }
    });

    return {
        validBooksInformation,
        invalidBooksInformation,
    };
};

export const isShownSpinner = (currentBookResult, isTroRemarksUpdating) =>
    isEmpty(currentBookResult) || isTroRemarksUpdating;

export const isNotFetchedTroData = (currentBookResult, data) =>
    !isEmpty(currentBookResult) && isEmpty(data);
export const getToolbarType = (endWordPosition, endWordTime, toolbarType) =>
    !isNil(endWordPosition) && isNil(endWordTime) ? toolbarType : null;
export const getBookById = (books, bookId) => books.find(book => book.id === bookId);
export const getEndWordData = (isUntranscribable, endWordPosition, endWordTime) => ({
    endPosition: isUntranscribable ? endWordPosition : null,
    endTime: isUntranscribable ? endWordTime : null,
    isSaveButtonDisabled: !(isUntranscribable && !isNil(endWordTime)),
});

export const getEditingNotification = (
    isChosenSchoolWindowDatesStrictlyEnforced,
    currentPeriod,
    windowDates,
    canEdit,
    toyPeriods,
) => {
    if (!isChosenSchoolWindowDatesStrictlyEnforced && currentPeriod === PERIOD.EOY && canEdit) {
        return TRO_EOY_CAN_EDIT_TEXT;
    }
    const daysLeft = isChosenSchoolWindowDatesStrictlyEnforced
        ? differenceInCalendarDays(new Date(windowDates[currentPeriod].endDate), Date.now())
        : differenceInCalendarDays(
              new Date(windowDates[getNextPeriod(currentPeriod, toyPeriods)].startDate),
              Date.now(),
          ) - 1;

    return daysLeft > 0 && canEdit ? (
        <>{`${daysLeft} day${getWordEnding(daysLeft)} left to edit the results`}</>
    ) : (
        CANNOT_BE_EDIT_MESSAGE
    );
};

export const getTroBannerContent = (isUntranscribable, complexityLevel) => {
    if (isUntranscribable) {
        return UNTRANSCRIBABLE_BANNER;
    }

    return complexityLevel === ON_GRADE_TEXT ? ON_GRADE_TEXT_BANNER : HIGH_OR_LOW_TEXT_BANNER;
};

const correctHandler = (marks, currentMark, accumulator) => {
    const { eventDetails } = currentMark;
    let updatedAccumulator = [...accumulator];
    const { startIsInsert, endIsInsert } = eventDetails;
    const isInsertCorection = startIsInsert && endIsInsert;
    const isVRMarkExist = isInsertCorection
        ? marks.some(mark => mark.eventType === INSERT && mark.eventDetails.source === VR_EVENT)
        : marks.some(mark => mark.eventType !== INSERT && mark.eventDetails.source === VR_EVENT);
    const isCorrectExist = updatedAccumulator.some(
        mark =>
            mark.eventType === CORRECT &&
            mark.eventDetails.startIsInsert === startIsInsert &&
            mark.eventDetails.endIsInsert === endIsInsert,
    );
    updatedAccumulator = isInsertCorection
        ? updatedAccumulator.filter(mark => mark.eventType !== INSERT)
        : updatedAccumulator.filter(
              mark => mark.eventType === INSERT || mark.eventType === CORRECT,
          );
    return {
        updatedMarks: isVRMarkExist && !isCorrectExist ? [currentMark] : [],
        updatedAccumulator,
    };
};

const omitSubstituteHandler = (marks, currentMark, accumulator) => {
    const { eventDetails, eventType } = currentMark;
    const isUserMark = eventDetails.source === USER_EVENT;
    const VROmitMark = marks.find(
        mark => mark.eventType === OMIT && mark.eventDetails.source === VR_EVENT,
    );
    const isVROmitMarkNeeded = isUserMark && eventType === OMIT && VROmitMark;
    let updatedAccumulator = [...accumulator];
    if (isUserMark) {
        updatedAccumulator = updatedAccumulator.filter(
            mark =>
                mark.eventType === INSERT ||
                (mark.eventType === CORRECT &&
                    mark.eventDetails.startIsInsert &&
                    mark.eventDetails.endIsInsert),
        );
    }
    return {
        updatedMarks: isVROmitMarkNeeded ? [] : [currentMark],
        updatedAccumulator: [...(isVROmitMarkNeeded ? [VROmitMark] : []), ...updatedAccumulator],
    };
};

const insertHandler = (currentMark, accumulator) => ({
    updatedMarks: [currentMark],
    updatedAccumulator:
        currentMark.eventDetails.source === USER_EVENT
            ? [...accumulator].filter(mark => mark.eventType !== INSERT)
            : [...accumulator],
});

const mergeMarks = (marks = []) =>
    marks.reduce((accumulator, currentMark) => {
        let reduceObject = {};

        switch (currentMark.eventType) {
            case OMIT:
            case SUBSTITUTE:
                reduceObject = omitSubstituteHandler(marks, currentMark, accumulator);

                break;
            case INSERT:
                reduceObject = insertHandler(currentMark, accumulator);

                break;
            case CORRECT:
                reduceObject = correctHandler(marks, currentMark, accumulator);

                break;
            default: {
                reduceObject = {
                    updatedMarks: [],
                    updatedAccumulator: [...accumulator],
                };
            }
        }

        return [...reduceObject.updatedAccumulator, ...reduceObject.updatedMarks];
    }, []);

export const getSentencesWithMergedMarks = listOfSentences =>
    listOfSentences.map(word => {
        return {
            ...word,
            marks: mergeMarks(word.marks),
        };
    });

export const getUserEventsWithFormatting = (sentences, endPosition) =>
    sentences.reduce((accumulator, currentValue) => {
        const marks = get(currentValue, 'marks', []);
        const mergedMarks = mergeMarks(marks);
        const userMarks = mergedMarks.filter(
            ({ eventDetails }) =>
                eventDetails.source === USER_EVENT &&
                (eventDetails.position <= endPosition || eventDetails.startPosition <= endPosition),
        );
        const parsedEvents = userMarks.map(mark => ({
            event_type: mark.eventType,
            event_details: convertKeysFromCamelToUndescore(mark.eventDetails),
        }));
        return [...accumulator, ...parsedEvents];
    }, []);

export const isSaveButtonDisabled = (originSentences, newSentences, endTime) => {
    const newSentencesWithMergedMarks = getSentencesWithMergedMarks(newSentences);
    return isNil(endTime)
        ? !originSentences.some(({ number, marks = [] }) => {
              const newMergedMarks = get(
                  newSentencesWithMergedMarks.find(sentence => sentence.number === number),
                  'marks',
                  [],
              );
              return !isEqual(marks, newMergedMarks);
          })
        : false;
};

export const isNeedAdditionalBook = books => {
    const parsedBooks = books.map(book => ({
        ...book,
        isValid: book.is_valid,
    }));
    const { validBooksInformation, invalidBooksInformation } =
        splitBooksInformationByValid(parsedBooks);

    return validBooksInformation.length === 1 && invalidBooksInformation.length === 1;
};

const isPerfomanceLevelChanged = (currentResult, updatedInterpretation) =>
    currentResult.measureResult.interpretation !== updatedInterpretation;

export const getConfirmationModalType = ({
    modalType,
    currentResult,
    updatedResult,
    complexityLevel,
}) => {
    if (modalType) return modalType;
    const isOnGrade = complexityLevel === ON_GRADE_TEXT;
    const { interpretation: updatedInterpretation, book_results: updatedBooks } = get(
        updatedResult,
        'measure_result',
    );
    const isAdditionalBookRequired = isNeedAdditionalBook(updatedBooks);
    const isFinalScoreChanged =
        !isOnGrade && isPerfomanceLevelChanged(currentResult, updatedInterpretation);

    if (isAdditionalBookRequired || isFinalScoreChanged) {
        return isFinalScoreChanged ? CHANGED_PERFOMANCE_MODAL : ADDITIONAL_BOOK_MODAL;
    }

    return null;
};
